import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { Portal } from '../../utils/Portal';
import { cn } from '../../utils/cn';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)]',
};

export function Modal({ isOpen, onClose, size = 'md', title, description, hideCloseButton = false, className, children, footer }) {
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          aria-hidden
          onClick={onClose}
          className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] animate-fade-in"
        />
        {/* Panel */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || 'Dialogue'}
          className={cn(
            'relative w-full bg-surface border border-border rounded-xl shadow-xl animate-scale-in',
            SIZES[size],
            className,
          )}
        >
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="absolute top-3 right-3 p-1.5 rounded-md text-mute hover:text-text hover:bg-bg-2"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
          {(title || description) && (
            <header className="px-6 pt-5 pb-3 border-b border-border">
              {title && <h2 className="text-lg font-bold tracking-tight pr-8">{title}</h2>}
              {description && <p className="text-sm text-mute mt-1 leading-relaxed">{description}</p>}
            </header>
          )}
          <div className="px-6 py-5">{children}</div>
          {footer && <footer className="px-6 py-4 border-t border-border bg-bg-2/50 rounded-b-xl flex items-center justify-end gap-2">{footer}</footer>}
        </div>
      </div>
    </Portal>
  );
}

export default Modal;
